import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Tooltip } from 'primeng/tooltip';
import { GlobalConstants } from 'src/app/base/global.constants';

/**
 * Add [ellipsisTooltip]="text" to any element that uses CSS ellipsis or line-clamp.
 * A PrimeNG bp-dark-tooltip is shown automatically only when the content is actually truncated.
 *
 * Usage:
 *   <span class="my-clamped-text" [ellipsisTooltip]="item.name">{{ item.name }}</span>
 */
@Directive({
  selector: '[ellipsisTooltip]',
  standalone: false, //NOSONAR
  hostDirectives: [{ directive: Tooltip }]
})
export class EllipsisTooltipDirective implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly tooltip = inject(Tooltip, { self: true });

  @Input('ellipsisTooltip') tooltipText: string | null = null;

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private updateTimer: ReturnType<typeof setTimeout> | undefined;

  private containsHtmlMarkup(text: string): boolean {
    return /<[^>]+>/.test(text);
  }

  ngOnInit(): void {
    this.tooltip.tooltipPosition = GlobalConstants.tooltipPosition ?? 'top';
    this.tooltip.tooltipStyleClass = 'bp-dark-tooltip';
    this.tooltip.appendTo = 'body';
    this.tooltip.showDelay = GlobalConstants.tooltipShowDelay ?? 300;
    this.tooltip.disabled = true;

    if (typeof this.tooltip.setOption === 'function') {
      this.tooltip.setOption({
        tooltipPosition: this.tooltip.tooltipPosition,
        tooltipStyleClass: this.tooltip.tooltipStyleClass,
        appendTo: this.tooltip.appendTo,
        showDelay: this.tooltip.showDelay,
        disabled: true,
        tooltipLabel: null
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltipText']) {
      this.scheduleUpdate();
    }
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.el.nativeElement);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.scheduleUpdate());
      this.mutationObserver.observe(this.el.nativeElement, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }

    this.scheduleUpdate();
  }

  ngOnDestroy(): void {
    clearTimeout(this.updateTimer);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleUpdate();
  }

  private scheduleUpdate(): void {
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => this.updateTooltipState(), 0);
  }

  private updateTooltipState(): void {
    const host = this.el.nativeElement;
    const text = this.tooltipText?.trim() || host.textContent?.trim() || '';
    const truncated = this.isTruncated(host);
    const escape = !this.containsHtmlMarkup(text);
    const disabled = !truncated || !text;

    this.tooltip.content = truncated ? text : '';
    this.tooltip.escape = escape;
    this.tooltip.disabled = disabled;

    if (typeof this.tooltip.setOption === 'function') {
      this.tooltip.setOption({
        tooltipLabel: truncated ? text : null,
        escape,
        disabled
      });
    }
  }

  private isTruncated(host: HTMLElement): boolean {
    return host.scrollHeight > host.clientHeight + 1 || host.scrollWidth > host.clientWidth + 1;
  }
}
